import { Injectable } from '@angular/core';
import { filterFrag } from './types/filter.types';
import { GridInput } from '@it2go/data-grid';
import {
    serviceDoctorRecordTypeItemFrag,
    serviceGroupItemFrag,
    serviceSpecialistContactTypeItemFrag,
    serviceSpecialistWorkTypeItemFrag,
    serviceWaitingListItemFrag,
    serviceWorkgroupItemFrag,
} from './types/service-settings.types';
import { GqlQueryOptions, GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { ServiceGroupGridObject } from '@libs/types/src/services/object-type/service/service-group.object';
import { ServiceSettingsUpdateInput } from '@libs/types/src/services/input-type/services/service-settings.update-input';
import { ServiceSettingsGridObject } from '@libs/types/src/services/object-type/service/service-settings.object';
import {
    ServiceWorkgroupGridObject,
} from '@libs/types/src/services/object-type/service/settings/service-workgroup.object';
import {
    ServiceDoctorRecordTypeGridObject,
} from '@libs/types/src/services/object-type/service/settings/service-docker-record-type.object';
import {
    ServiceSpecialistRecordWorkTypeCreateInput,
} from '@libs/types/src/services/input-type/services/settings/service-specialist-record-work-type.create-input';
import {
    ServiceSpecialistRecordWorkTypeUpdateInput,
} from '@libs/types/src/services/input-type/services/settings/service-specialist-record-work-type.update-input';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import {
    ServiceSpecialistRecordContactTypeCreateInput,
} from '@libs/types/src/services/input-type/services/settings/service-specialist-record-contact-type.create-input';
import {
    ServiceSpecialistRecordContactTypeUpdateInput,
} from '@libs/types/src/services/input-type/services/settings/service-specialist-record-contact-type.update-input';
import {
    ServiceDoctorRecordTypeCreateInput,
} from '@libs/types/src/services/input-type/services/settings/service-doctor-record-type.create-input';
import {
    ServiceDoctorRecordTypeUpdateInput,
} from '@libs/types/src/services/input-type/services/settings/service-doctor-record-type.update-input';
import {
    ServiceVisitTypeUpdateInput,
} from '@libs/types/src/services/input-type/services/settings/service-visit-type.update-input';
import {
    ServiceVisitTypeCreateInput,
} from '@libs/types/src/services/input-type/services/settings/service-visit-type.create-input';
import {
    ServiceMailsCreateInput,
} from '@libs/types/src/services/input-type/services/settings/service-mails.create-input';
import {
    ServiceMailsUpdateInput,
} from '@libs/types/src/services/input-type/services/settings/service-mails.update-input';
import {
    ServiceOperationalCapacityGridObject,
} from '@libs/types/src/services/object-type/service/settings/service-operational-capacity.object';
import { ServiceGroupCreateInput } from '@libs/types/src/services/input-type/services/service-group.create-input';
import { ServiceGroupUpdateInput } from '@libs/types/src/services/input-type/services/service-group.update-input';
import { ServiceLocationCreateInput } from '@libs/types/src/services/input-type/services/service-location.create-input';
import { ServiceLocationUpdateInput } from '@libs/types/src/services/input-type/services/service-location.update-input';
import { OkObject } from '@it2go/types';
import {
    ServiceOperationalCapacityCreateInput,
} from '@libs/types/src/services/input-type/services/settings/service-operational-capacity.create-input';
import {
    ServiceOperationalCapacityUpdateInput,
} from '@libs/types/src/services/input-type/services/settings/service-operational-capacity.update-input';
import {
    ServiceSpecialistRecordWorkTypeGridObject,
} from '@libs/types/src/services/object-type/service/settings/service-specialist-record-work-type.object';
import {
    ServiceVisitTypeGridObject,
} from '@libs/types/src/services/object-type/service/settings/service-visit-type.object';
import {
    ServiceMailsNextNumberInput,
} from '@libs/types/src/services/input-type/services/settings/service-mails.next-number-input';
import {
    ServiceMailsSearchRecipientInput,
} from '@libs/types/src/services/input-type/services/settings/service-mails.search-recipient-input';
import {
    ServiceWaitingListCreateInput,
} from '@libs/types/src/services/input-type/services/settings/service-waiting-list-create.input';
import {
    ServiceWaitingListUpdateInput,
} from '@libs/types/src/services/input-type/services/settings/service-waiting-list-update.input';
import {
    ServiceWaitingListGridObject,
} from '@libs/types/src/services/object-type/service/settings/service-waiting-list.object';
import {
    ServiceSocialAreaGridObject,
    ServiceSocialAreaObject,
} from '@libs/types/src/services/object-type/service/social-service/service-social-area.object';
import {
    ServiceSocialAreaUpsertInput,
} from '@libs/types/src/services/input-type/services/social-service/service-social-area.upsert-input';
import {
    ServiceComplaintTypeGridObject,
} from '@libs/types/src/services/object-type/service/settings/service-complaint-type.object';
import {
    ServiceComplaintTypeCreateInput,
} from '@libs/types/src/services/input-type/services/settings/service-complaint-type.create-input';
import {
    ServiceComplaintTypeUpdateInput,
} from '@libs/types/src/services/input-type/services/settings/service-complaint-type.update-input';
import {
    SocialPaymentSequenceGridObject,
    SocialPaymentSequenceObject,
} from '@libs/types/src/services/object-type/service/settings/social-payment-sequence.object';
import {
    SocialPaymentSequenceCreateInput,
} from '@libs/types/src/services/input-type/services/settings/social-payment-sequence.create-input';
import {
    SocialPaymentSequenceUpdateInput,
} from '@libs/types/src/services/input-type/services/settings/social-payment-sequence.update-input';

@Injectable({ providedIn: 'root' })
export class ServiceSettingsGqlServiceV2 extends GqlServiceV2 {

    public getServiceSocialAreas(input: GridInput, fragment: string) {
        return this.query<ServiceSocialAreaGridObject>(
            `
                query($input: GridInput!) {
                    serviceSocialArea {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public upsertServiceSocialAreas(input: ServiceSocialAreaUpsertInput, fragment: string) {
        return this.mutate<ServiceSocialAreaObject>(
            `
                mutation($input: ServiceSocialAreaUpsertInput!) {
                    serviceSocialArea {
                        upsert(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceSocialArea(input: IdInput, fragment: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    serviceSocialArea {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceGroups(input: GridInput, fragment: string = serviceGroupItemFrag) {
        return this.query<ServiceGroupGridObject>(
            `
                query($input: GridInput) {
                    serviceGroup {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServiceGroup(input: ServiceGroupCreateInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceGroupCreateInput!) {
                    serviceGroup {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServiceGroup(input: ServiceGroupUpdateInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceGroupUpdateInput!) {
                    serviceGroup {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceGroup(input: IdInput, frag = 'ok') {
        return this.mutate(
            `
                mutation($input: IdInput!) {
                    serviceGroup {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceSettings(input: GridInput): Promise<ServiceSettingsGridObject> {
        return this.query<ServiceSettingsGridObject>(
            `
                query($input: GridInput) {
                    serviceSettings {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                id
                                key
                                data
                                serviceId
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServiceSettings(input: ServiceSettingsUpdateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceSettingsUpdateInput!) {
                    serviceSettings {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceWorkgroups(input: GridInput): Promise<ServiceWorkgroupGridObject> {
        return this.query(
            `
                query($input: GridInput) {
                    serviceWorkgroup {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${serviceWorkgroupItemFrag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceSpecialistRecordWorkTypes(
        input: GridInput,
        fragment: string = serviceSpecialistWorkTypeItemFrag,
        options: GqlQueryOptions = {},
    ) {
        return this.query<ServiceSpecialistRecordWorkTypeGridObject>(
            `
                query($input: GridInput) {
                    serviceSpecialistRecordWorkType {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
            options,
        );
    }

    public createServiceSpecialistRecordWorkType(input: ServiceSpecialistRecordWorkTypeCreateInput) {
        return this.mutate(
            `
                mutation($input: ServiceSpecialistRecordWorkTypeCreateInput!) {
                    serviceSpecialistRecordWorkType {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServiceSpecialistRecordWorkType(input: ServiceSpecialistRecordWorkTypeUpdateInput) {
        return this.mutate(
            `
                mutation($input: ServiceSpecialistRecordWorkTypeUpdateInput!) {
                    serviceSpecialistRecordWorkType {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceSpecialistRecordWorkType(input: IdInput) {
        return this.mutate(
            `
                mutation($input: IdInput!) {
                    serviceSpecialistRecordWorkType {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceSpecialistRecordContactTypes(
        input: GridInput,
        fragment: string = serviceSpecialistContactTypeItemFrag,
        options: GqlQueryOptions = {},
    ) {
        return this.query(
            `
                query($input: GridInput) {
                    serviceSpecialistRecordContactType {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
            options,
        );
    }

    public createServiceSpecialistRecordContactType(input: ServiceSpecialistRecordContactTypeCreateInput) {
        return this.mutate(
            `
                mutation($input: ServiceSpecialistRecordContactTypeCreateInput!) {
                    serviceSpecialistRecordContactType {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServiceSpecialistRecordContactType(input: ServiceSpecialistRecordContactTypeUpdateInput) {
        return this.mutate(
            `
                mutation($input: ServiceSpecialistRecordContactTypeUpdateInput!) {
                    serviceSpecialistRecordContactType {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceSpecialistRecordContactType(input: IdInput) {
        return this.mutate(
            `
                mutation($input: IdInput!) {
                    serviceSpecialistRecordContactType {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceMails(input: GridInput, fragment: string) {
        return this.query(
            `
            query($input: GridInput) {
                serviceMails {
                    filter(input: $input) {
                        ${filterFrag}
                        items {
                            ${fragment}
                        }
                    }
                }
            }
        `,
            { input },
        );
    }

    public getServiceMailNextNumber(input: ServiceMailsNextNumberInput): Promise<number> {
        return this.query(
            `
                query($input: ServiceMailsNextNumberInput!) {
                    serviceMails {
                        nextMailNumber(input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public getServiceMailRecipients(input: ServiceMailsSearchRecipientInput): Promise<string[]> {
        return this.query(
            `
                query($input: ServiceMailsSearchRecipientInput!) {
                    serviceMails {
                        recipients(input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public createServiceMails(input: ServiceMailsCreateInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate(
            `
            mutation($input: ServiceMailsCreateInput!) {
                serviceMails {
                    create(input: $input) {
                        ${fragment}
                    }
                }
            }
        `,
            { input },
        );
    }

    public updateServiceMails(input: ServiceMailsUpdateInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate(
            `
            mutation($input: ServiceMailsUpdateInput!) {
                serviceMails {
                    update(input: $input) {
                        ${fragment}
                    }
                }
            }
        `,
            { input },
        );
    }

    public deleteServiceMails(input: IdInput, fragment: string = 'ok') {
        return this.mutate(
            `
            mutation($input: IdInput!) {
                serviceMails {
                    delete(input: $input) {
                        ${fragment}
                    }
                }
            }
        `,
            { input },
        );
    }

    public getServiceDoctorRecordTypes(input: GridInput): Promise<ServiceDoctorRecordTypeGridObject> {
        return this.query(
            `
                query($input: GridInput) {
                    serviceDoctorRecordType {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${serviceDoctorRecordTypeItemFrag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServiceDoctorRecordType(input: ServiceDoctorRecordTypeCreateInput) {
        return this.mutate(
            `
                mutation($input: ServiceDoctorRecordTypeCreateInput!) {
                    serviceDoctorRecordType {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServiceDoctorRecordType(input: ServiceDoctorRecordTypeUpdateInput) {
        return this.mutate(
            `
                mutation($input: ServiceDoctorRecordTypeUpdateInput!) {
                    serviceDoctorRecordType {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceDoctorRecordType(input: IdInput) {
        return this.mutate(
            `
                mutation($input: IdInput!) {
                    serviceDoctorRecordType {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceVisitTypes(input: GridInput, fragment: string, options: GqlQueryOptions = {}) {
        return this.query<ServiceVisitTypeGridObject>(
            `
                query($input: GridInput) {
                    serviceVisitType {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
            options,
        );
    }

    public createServiceVisitType(input: ServiceVisitTypeCreateInput) {
        return this.mutate(
            `
                mutation($input: ServiceVisitTypeCreateInput!) {
                    serviceVisitType {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServiceVisitType(input: ServiceVisitTypeUpdateInput) {
        return this.mutate(
            `
                mutation($input: ServiceVisitTypeUpdateInput!) {
                    serviceVisitType {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceVisitType(input: IdInput) {
        return this.mutate(
            `
                mutation($input: IdInput!) {
                    serviceVisitType {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceOperationalCapacities(input: GridInput, fragment: string) {
        return this.query<ServiceOperationalCapacityGridObject>(
            `
                query($input: GridInput) {
                    serviceOperationalCapacity {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServiceOperationalCapacity(input: ServiceOperationalCapacityCreateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceOperationalCapacityCreateInput!) {
                    serviceOperationalCapacity {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServiceOperationalCapacity(input: ServiceOperationalCapacityUpdateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceOperationalCapacityUpdateInput!) {
                    serviceOperationalCapacity {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceOperationalCapacity(input: IdInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    serviceOperationalCapacity {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceWaitingListItems(input: GridInput) {
        return this.query<ServiceWaitingListGridObject>(
            `
                query($input: GridInput) {
                    serviceWaitingList {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${serviceWaitingListItemFrag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServiceWaitingListItem(input: ServiceWaitingListCreateInput) {
        return this.mutate(
            `
                mutation($input: ServiceWaitingListCreateInput!) {
                    serviceWaitingList {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServiceWaitingListItem(input: ServiceWaitingListUpdateInput) {
        return this.mutate(
            `
                mutation($input: ServiceWaitingListUpdateInput!) {
                    serviceWaitingList {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceWaitingListItem(input: IdInput) {
        return this.mutate(
            `
                mutation($input: IdInput!) {
                    serviceWaitingList {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServiceLocation(input: ServiceLocationCreateInput) {
        return this.mutate<OkObject>(
            `
            mutation($input: ServiceLocationCreateInput!) {
                serviceLocation {
                    create(input: $input) {
                        ok
                    }
                }
            }
        `,
            { input },
        );
    }

    public updateServiceLocation(input: ServiceLocationUpdateInput) {
        return this.mutate<OkObject>(
            `
            mutation($input: ServiceLocationUpdateInput!) {
                serviceLocation {
                    update(input: $input) {
                        ok
                    }
                }
            }
        `,
            { input },
        );
    }

    public deleteServiceLocation(input: IdInput) {
        return this.mutate<OkObject>(
            `
            mutation($input: IdInput!) {
                serviceLocation {
                    delete(input: $input) {
                        ok
                    }
                }
            }
        `,
            { input },
        );
    }

    public getComplaintTypes(input: GridInput, fragment: string) {
        return this.query<ServiceComplaintTypeGridObject>(
            `
                query($input: GridInput!) {
                    serviceComplaintType {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createComplaintType(input: ServiceComplaintTypeCreateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceComplaintTypeCreateInput!) {
                    serviceComplaintType {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateComplaintType(input: ServiceComplaintTypeUpdateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceComplaintTypeUpdateInput!) {
                    serviceComplaintType {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteComplaintType(input: IdInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    serviceComplaintType {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialPaymentSequences(input: GridInput, fragment: string) {
        return this.query<SocialPaymentSequenceGridObject>(
            `
                query($input: GridInput!) {
                    socialPaymentSequence {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async createSocialPaymentSequence(input: SocialPaymentSequenceCreateInput, fragment: string) {
        return this.mutate<SocialPaymentSequenceObject>(
            `
                mutation($input: SocialPaymentSequenceCreateInput!) {
                    socialPaymentSequence {
                        create(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async updateSocialPaymentSequence(input: SocialPaymentSequenceUpdateInput, fragment: string) {
        return this.mutate<SocialPaymentSequenceObject>(
            `
                mutation($input: SocialPaymentSequenceUpdateInput!) {
                    socialPaymentSequence {
                        update(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async deleteSocialPaymentSequence(input: IdInput, fragment: string = 'ok') {
        return this.mutate<SocialPaymentSequenceObject>(
            `
                mutation($input: IdInput!) {
                    socialPaymentSequence {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

}
